import { type MaybeRefOrGetter } from "vue";
import PatientsTreatmentService, {
  type IAllowedPracticesFilter,
  type IPatientJobsFilter,
  type IPatientTreatmentPlanFilter,
  type TTreatmentPlanWithAvailableJobs,
} from "@/api/models/patient/treatmentPlan";
import { ETreatmentPlanJobState, type TTreatmentPlanJob } from "@/api/models/treatmentPlan/job";
import { ETreatmentPlanJobSystemSignal } from "@/api/models/treatmentPlan/job";
import type { TActivateTreatmentPlan } from "@/api/models/treatmentPlan/treatmentPlan";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation.ts";
import { type TAllAvailablePractices } from "@/api/models/patient/patient";
import {
  createQueryKeys,
  useGenericDetailQuery,
  useGenericListQuery,
} from "@/composables/shared/queries";

export const PatientTreatmentPlanQueryKeys = createQueryKeys(
  PatientsTreatmentService.queryKey,
  (key) => ({
    availableTreatmentPlans: key<IPatientTreatmentPlanFilter>("available-treatment-plans"),
    allowedTreatmentForStaff: key("allowed-treatment-for-staff"),
    allowedPracticesForTreatmentType: key<IAllowedPracticesFilter>(
      "allowed-practices-for-treatment-type"
    ),
    treatmentPlansJobsOfPatient: key<IPatientTreatmentPlanFilter>(
      "treatment-plans-jobs-of-patient"
    ),
    listOfTreatmentJobs: key<IPatientJobsFilter>("list-of-treatment-jobs"),
  })
);

export const usePatientListOfAvailableTreatmentPlans = (
  filters: MaybeRefOrGetter<IPatientTreatmentPlanFilter>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<string, IPatientTreatmentPlanFilter>({
    queryKey: PatientTreatmentPlanQueryKeys.availableTreatmentPlans(),
    params: { filters, enabled },
    fetcher: (f) =>
      PatientsTreatmentService.listOfAvailableTreatmentPlans({ patientId: f!.patientId! }),
    neededKey: ["patientId"],
    permission: $permSubjects.PATIENT__PATIENT,
  });

export function useActivateTreatmentPlan() {
  return useGenericMutation<TActivateTreatmentPlan, TActivateTreatmentPlan & { patientId: string }>(
    {
      mutationFn: (variables) =>
        PatientsTreatmentService.activateTreatmentPlan(
          { patientId: variables.patientId },
          {
            treatment_type: variables.treatment_type,
            cooperate_parties: variables.cooperate_parties,
          }
        ),
      feature: "activate-treatment-plan",
    }
  );
}

export const useAllowedTreatmentForStaffQuery = (enabled: MaybeRefOrGetter<boolean> = true) =>
  useGenericListQuery<string>({
    queryKey: PatientTreatmentPlanQueryKeys.allowedTreatmentForStaff(),
    params: { enabled },
    fetcher: async () => {
      const res = await PatientsTreatmentService.listOfAvailableTreatmentForStaff({});
      // eslint-disable-next-line unicorn/no-array-sort
      return { ...res, data: [...res.data].sort((a, b) => a.localeCompare(b)) };
    },
    justQueryKey: true,
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
  });

export const useAllowedPracticesForTreatmentTypeQuery = (
  filters: MaybeRefOrGetter<IAllowedPracticesFilter>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericDetailQuery<TAllAvailablePractices, IAllowedPracticesFilter>({
    queryKey: PatientTreatmentPlanQueryKeys.allowedPracticesForTreatmentType(),
    params: { filters, enabled },
    fetcher: (f) =>
      PatientsTreatmentService.listOfAvailablePracticesForTreatmentType(
        {},
        { treatment_type: f!.treatment_type! }
      ),
    neededKey: ["treatment_type"],
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
  });

export const useTreatmentPlansJobsOfPatient = (
  filters: MaybeRefOrGetter<IPatientTreatmentPlanFilter>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TTreatmentPlanWithAvailableJobs, IPatientTreatmentPlanFilter>({
    queryKey: PatientTreatmentPlanQueryKeys.treatmentPlansJobsOfPatient(),
    params: { filters, enabled },
    fetcher: async (f) => {
      const res = await PatientsTreatmentService.listPatientTreatmentPlansWithAvailableJobs({
        patientId: f!.patientId!,
      });
      return {
        ...res,
        data: res.data.filter(
          (item) =>
            item.plan.state !== ETreatmentPlanJobState.PENDING ||
            item.jobs.every(
              (job) => job.signal_system_type === ETreatmentPlanJobSystemSignal.SELECT_TREATMENT
            )
        ),
      };
    },
    permission: $permSubjects.TREATMENT_PLAN__PATIENTTREATMENTJOB,
    neededKey: ["patientId"],
  });

export const usePatientListOfTreatmentJobs = (
  filters: MaybeRefOrGetter<IPatientJobsFilter>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TTreatmentPlanJob, IPatientJobsFilter>({
    queryKey: PatientTreatmentPlanQueryKeys.listOfTreatmentJobs(),
    params: { filters, enabled },
    fetcher: (f) => {
      return PatientsTreatmentService.listOfTreatmentJobs({ patientId: f!.patientId! }, f);
    },
    permission: $permSubjects.TREATMENT_PLAN__PATIENTTREATMENTJOB,
    neededKey: ["patientId"],
  });
